/**
 * O histórico do caso DENTRO da conversa — o segundo fio da mesma linha.
 *
 * A conversa mostrava só o que foi dito por mensagem. Tudo o que o escritório e
 * o sistema fizeram — contrato enviado, documento assinado, documento aprovado,
 * peça criada, caso aberto no Astrea, processo protocolado — ficava em `events`,
 * numa trilha que ninguém abre. O resultado é o que a auditoria de 22/08 viu no
 * vídeo: sete coisas acontecendo e a tela em silêncio.
 *
 * Aqui os eventos viram MARCOS: cartões no meio da conversa, no instante em que
 * aconteceram, visivelmente diferentes de uma bolha de WhatsApp (é o escritório
 * agindo, não alguém falando) e com o caminho para o que eles produziram — o
 * documento, a peça, o processo.
 *
 * Duas decisões que valem estar escritas:
 *
 *   1. **Nem todo evento é marco.** `campo`, `criacao` e `tarefa` são ruído na
 *      conversa: mudar o CPF da ficha não é acontecimento do caso, é digitação.
 *      Entra o que MUDA O ESTADO do processo — e é por isso que a lista é uma
 *      tabela, e não um `if` no componente.
 *   2. **O marco não é mensagem.** Ele não tem autor "do lado de cá", não entra
 *      na conta de não lidas e não emenda com a bolha vizinha. A conversa
 *      continua sendo a conversa; o marco é o que aconteceu enquanto ela
 *      acontecia.
 */

/* Só o que é acontecimento do caso. A chave é `tipo` do evento; o `de`/`para`
   afina o texto. */
const DESENHO = {
  assinatura: (e) => {
    if (e.para === "assinado") return { cor: "verde", titulo: `${e.texto ?? "Documento"} assinado`, icone: "assinatura" };
    if (e.para === "enviado") return { cor: "terra", titulo: e.texto ?? "Pacote de assinatura enviado", icone: "contrato" };
    if (e.para === "recusado") return { cor: "terra", titulo: `${e.texto ?? "Documento"} recusado pelo cliente`, icone: "risco" };
    if (e.para === "visto") return null; /* "abriu o link" é ruído: não mudou nada. */
    return { cor: "terra", titulo: e.texto ?? "Assinatura", icone: "contrato" };
  },
  documento: (e) => {
    const rotulo = e.texto ?? "Documento";
    if (e.para === "aprovado") return { cor: "verde", titulo: `${rotulo} aprovado`, icone: "ok" };
    if (e.para === "recebido")
      return { cor: "ambar", titulo: `${rotulo} chegou`, detalhe: "esperando conferência", icone: "anexo" };
    if (e.para === "ilegivel")
      return { cor: "terra", titulo: `${rotulo} ilegível`, detalhe: "pedido de novo ao cliente", icone: "risco" };
    if (e.para === "dispensado") return { cor: "cinza", titulo: `${rotulo} dispensado`, icone: "ok" };
    if (e.para === "faltante") return null; /* abrir o checklist não é evento por documento. */
    return { cor: "cinza", titulo: rotulo, icone: "anexo" };
  },
  etapa: (e, rotuloDaEtapa) => ({
    cor: "terra",
    titulo: `Etapa → ${rotuloDaEtapa(e.para)}`,
    detalhe: e.texto ?? "",
    icone: "etapa",
  }),
  peca: (e) => ({ cor: "roxo", titulo: `Peça: ${e.texto ?? e.para}`, icone: "peca", acao: "peca" }),
  protocolo: (e) => ({
    cor: "verde",
    titulo: `Processo protocolado — ${e.para}`,
    detalhe: e.texto ?? "",
    icone: "protocolo",
    acao: "processo",
  }),
  astrea: (e) => ({ cor: "azul", titulo: e.texto ?? "Astrea", icone: "astrea" }),
  reuniao: (e) => ({ cor: "roxo", titulo: e.texto ?? "Reunião de alinhamento", icone: "reuniao" }),
};

/**
 * Os marcos do caso, prontos para entrar na conversa.
 *
 * `rotuloDaEtapa` vem de fora porque a etapa é configurável em tempo de
 * execução: traduzir a chave aqui dentro seria fixar o funil de hoje.
 */
export function marcosDaTrilha(trilha = [], rotuloDaEtapa = (c) => c) {
  const marcos = [];
  for (const e of trilha) {
    const desenhar = DESENHO[e?.tipo];
    if (!desenhar) continue;
    const marco = desenhar(e, rotuloDaEtapa);
    if (!marco) continue;
    marcos.push({
      /* Id com prefixo: a conversa usa `id` como chave de React e de rolagem, e
         um evento nº 3 não pode colidir com a mensagem nº 3. */
      id: `marco-${e.id}`,
      tipo: "marco",
      direcao: "sistema",
      quando: e.criado_em,
      autor: e.autor ?? "sistema",
      marco,
    });
  }
  /* Do mais VELHO para o mais novo. `crm.timeline` devolve o contrário (é lista
     de trilha, e trilha se lê de cima para baixo), e a conversa se lê ao
     contrário disso: um marco fora de ordem aparece antes do que o causou. */
  return marcos.sort((a, b) => (a.quando < b.quando ? -1 : a.quando > b.quando ? 1 : 0));
}

/**
 * A conversa com os marcos no meio, na ordem do relógio.
 *
 * A mistura é por TEMPO e não por tipo: o marco "documento aprovado" tem de
 * aparecer depois da foto que o cliente mandou e antes da resposta que veio em
 * seguida, senão o histórico conta outra história.
 */
export function comMarcos(mensagens = [], trilha = [], rotuloDaEtapa) {
  const marcos = marcosDaTrilha(trilha, rotuloDaEtapa);
  if (!marcos.length) return mensagens;
  const quando = (m) => String(m?.quando ?? "");
  return [...mensagens, ...marcos].sort((a, b) => (quando(a) < quando(b) ? -1 : quando(a) > quando(b) ? 1 : 0));
}

/**
 * QUEM fez, em português — nunca a chave crua do banco.
 *
 * O marco da conversa terminava com "13:53 · webhook:liderhub": nome de
 * integração, com dois pontos no meio, na cara de quem atende. `autor` é campo
 * de AUDITORIA (é ele que a trilha guarda para responder "quem fez isso"), e a
 * chave serve para isso — mas o que se lê na conversa é uma frase.
 *
 * Pessoa continua com o próprio nome: é a resposta que importa quando existe.
 */
export function autorEmPalavras(autor) {
  const cru = String(autor ?? "").trim();
  if (!cru || cru === "sistema") return "sistema";
  /* Integração: o cliente não mandou nada para o "webhook", mandou pelo
     WhatsApp — e é isso que quem lê a conversa precisa saber. */
  if (/^webhook\b/i.test(cru)) return "pelo WhatsApp";
  if (/^(copiloto|ia)\b/i.test(cru) || /^IA \(autom/i.test(cru)) return "pela IA";
  /* `fluxo:Triagem 24/7` — o nome do fluxo é o que diz alguma coisa. */
  const doFluxo = /^fluxo:(.+)$/i.exec(cru);
  if (doFluxo) return `pelo fluxo “${doFluxo[1].trim()}”`;
  const doAgente = /^agente:(.+)$/i.exec(cru);
  if (doAgente) return `pelo assistente ${doAgente[1].trim()}`;
  if (cru === "semente" || cru === "demonstracao") return "da demonstração";
  if (cru === "astrea") return "pelo Astrea";
  if (cru === "regua") return "pela régua de cobrança";
  return cru;
}
